import { ThunkDispatch } from "@reduxjs/toolkit";
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { Stage, Layer, Image as KonvaImage, Transformer } from "react-konva";
import Konva from "konva";

import {
  floorPlanExtractionActions,
  FloorPlanImage,
  FloorPlanAfterImage,
  BandTemplate,
  LocalBandTemplate,
  PdfConvertedPage,
} from "../../reducers/floorPlanExtraction";
import { selectFloorPlanAfterImageData } from "../../selectors";
import { Modal } from "flowbite-react";
import { CloseModalIcon, ConfirmGenerateAfterPhotoIcon } from "../SVGIcons";
// 抽出したコンポーネント・フック・ユーティリティ
import { EditorTool, EditorObject } from "./types/editorTypes";
import { useLoadImage, useBandTemplateManagement, useCanvasDrawing } from "./hooks";
import { getContentBounds, downloadCanvasAsImage, downloadCanvasAsPDF, downloadMultiPagePDF, renderPageOffScreen } from "./utils/canvasExport";
import { EditorToolbar } from "./toolbar";
import { BandTemplatePanel, BandTemplateSelectPopup, NewTemplateForm } from "./band";
import { FileUploadDropzone, DownloadDropdown, TextEditOverlay } from "./ui";
import { PDFPageSelectModal } from "./ui/PDFPageSelectModal";
import { isPDFFile, convertPDFPageToImage, clearPDFCache } from "../../utils/pdfToImage";
import { renderEditorObject, renderDrawingPreview, ObjectHandlers, Watermark } from "./canvas";
import { HintPopup } from "../common/HintPopup";
import { PlanTypeExtension } from "../../constants";

// 履歴状態の型定義（オブジェクト + 帯設定）
interface HistoryState {
  objects: EditorObject[];
  bandTemplateId: number | null;
  localBandTemplateId: string | null;
  bandPosition: 'top' | 'bottom';
  bandXOffset: number;
  bandYOffset: number;
  bandScale: number;
  bandScaleY: number;
}

// State型定義
interface RootState {
  floorPlanExtraction: {
    isLoading: boolean;
    bandSourceImage: FloorPlanImage | null;
    bandInputSource: 'upload' | 'extraction_result';
    bandTemplates: BandTemplate[];
    bandTemplatesCount: number;
    bandTemplatesMaxCount: number;
    bandTemplatesLoading: boolean;
    localBandTemplate: LocalBandTemplate | null;
    selectedBandTemplate: BandTemplate | null;
    bandPosition: 'top' | 'bottom';
    bandXOffset: number;
    bandYOffset: number;
    bandScale: number;
    bandScaleY: number;
    bandResultImage: FloorPlanAfterImage | null;
    afterImages: FloorPlanAfterImage[];
    runTime: number;
    isPhotoTooBigPopupShow: boolean;
    pdfConvertedPages: PdfConvertedPage[];
    currentPdfPageIndex: number;
    [key: string]: unknown;
  };
  currentUser: {
    creditConsumed: number;
    creditLimit: number;
    isLimitInfinity: boolean;
    planType: string | undefined;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const extractionResult = selectFloorPlanAfterImageData(state);
  return {
    ...state.floorPlanExtraction,
    ...state.currentUser,
    extractionResult,
  };
}

// コンポーネントのProps型定義
interface BandReplacementFormSomeOwnProps {
  useExtractionResult?: boolean;
}

interface BandReplacementFormSomeProps extends BandReplacementFormSomeOwnProps {
  isLoading: boolean;
  bandSourceImage: FloorPlanImage | null;
  bandInputSource: 'upload' | 'extraction_result';
  bandTemplates: BandTemplate[];
  bandTemplatesCount: number;
  bandTemplatesMaxCount: number;
  bandTemplatesLoading: boolean;
  localBandTemplate: LocalBandTemplate | null;
  selectedBandTemplate: BandTemplate | null;
  bandPosition: 'top' | 'bottom';
  bandXOffset: number;
  bandYOffset: number;
  bandScale: number;
  bandScaleY: number;
  bandResultImage: FloorPlanAfterImage | null;
  extractionResult: FloorPlanAfterImage | null;
  runTime: number;
  isPhotoTooBigPopupShow: boolean;
  creditConsumed: number;
  creditLimit: number;
  isLimitInfinity: boolean;
  planType: string | undefined;
  pdfConvertedPages: PdfConvertedPage[];
  currentPdfPageIndex: number;
}

const BandReplacementFormSome = (props: BandReplacementFormSomeProps) => {
  const {
    isLoading,
    bandSourceImage,
    bandTemplates,
    bandTemplatesCount,
    bandTemplatesMaxCount,
    localBandTemplate,
    selectedBandTemplate,
    bandPosition,
    bandXOffset,
    bandYOffset,
    bandScale,
    bandScaleY,
    extractionResult,
    useExtractionResult,
    isPhotoTooBigPopupShow,
    creditConsumed,
    creditLimit,
    isLimitInfinity,
    planType,
    pdfConvertedPages,
    currentPdfPageIndex,
  } = props;

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // キャンバス関連
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const bandImageRef = useRef<Konva.Image>(null);

  // 使用する画像を決定
  const sourceImageUrl = useExtractionResult
    ? extractionResult?.serverURI
    : bandSourceImage?.uri;

  // 画像ロード（useCanvasDrawingより前に配置）
  const sourceImg = useLoadImage(sourceImageUrl);

  // 編集ツール
  const [currentTool, setCurrentTool] = useState<EditorTool>('select');

  // PDF関連の状態
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  // ページごとのオブジェクトを保存
  const [pageObjectsMap, setPageObjectsMap] = useState<Record<number, EditorObject[]>>({});
  // ページごとの帯設定を保存
  interface PageBandSettings {
    bandTemplateId: number | null;
    localBandTemplateId: string | null;
    bandPosition: 'top' | 'bottom';
    bandXOffset: number;
    bandYOffset: number;
    bandScale: number;
    bandScaleY: number;
  }
  const [pageBandSettingsMap, setPageBandSettingsMap] = useState<Record<number, PageBandSettings>>({});
  // pageBandSettingsMapのrefバージョン（同期的にアクセスするため）
  const pageBandSettingsMapRef = useRef<Record<number, PageBandSettings>>({});

  // 最新の帯設定を追跡するref（ページ切り替え時に最新の値を確実に取得するため）
  const latestBandSettingsRef = useRef({
    bandTemplateId: null as number | null,
    localBandTemplateId: null as string | null,
    bandPosition: 'top' as 'top' | 'bottom',
    bandXOffset: 0,
    bandYOffset: 0,
    bandScale: 1.0,
    bandScaleY: 1.0,
  });

  // テキスト設定
  const [textColor, setTextColor] = useState('#000000');
  const [textBgColor, setTextBgColor] = useState('transparent');
  const [newText, setNewText] = useState('');
  const [textFontFamily, setTextFontFamily] = useState("'Hiragino Mincho Pro', 'Hiragino Mincho ProN', serif");
  const [textFontSize, setTextFontSize] = useState(16);
  const [textFontStyle, setTextFontStyle] = useState<'normal' | 'bold' | 'italic'>('normal');
  const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right' | 'justify'>('left');
  const [verticalAlign, setVerticalAlign] = useState<'top' | 'middle' | 'bottom'>('top');

  // マーカー設定
  const [markerColor, setMarkerColor] = useState('#000000');
  const [markerStrokeWidth, setMarkerStrokeWidth] = useState<1 | 2 | 3 | 4 | 8 | 16 | 32>(8); // 1px, 2px, 3px, 4px, 8px, 16px, 32px

  // 四角形設定（useCanvasDrawingより前に定義が必要）
  const [rectFillEnabled, setRectFillEnabled] = useState(true);
  const [rectFillColor, setRectFillColor] = useState('#ffffff');
  const [rectStrokeEnabled, setRectStrokeEnabled] = useState(false);
  const [rectStrokeColor, setRectStrokeColor] = useState('#000000');

  // ダウンロード中フラグ（透かしを非表示にするため）
  const [isDownloading, setIsDownloading] = useState(false);

  // Undo/Redo履歴管理（オブジェクト + 帯設定を含む）
  const initialHistoryState: HistoryState = {
    objects: [],
    bandTemplateId: null,
    localBandTemplateId: null,
    bandPosition: 'top',
    bandXOffset: 0,
    bandYOffset: 0,
    bandScale: 1.0,
    bandScaleY: 1.0,
  };
  const [history, setHistory] = useState<HistoryState[]>([initialHistoryState]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const isUndoRedoRef = useRef(false);

  // キャンバス描画フック
  const {
    objects,
    setObjects,
    selectedId,
    setSelectedId,
    isDrawing,
    currentPoints,
    canvasSize,
    displaySize,
    cursorVisible,
    loadedImages,
    editingTextId,
    setEditingTextId,
    editingTextValue,
    setEditingTextValue,
    editingTextPosition,
    setEditingTextPosition,
    cursorPosition,
    setCursorPosition,
    textareaRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleStageClick,
    handleObjectSelect,
    handleDragEnd,
    handleDeleteSelected,
    handleTextDoubleClick,
    handleRectDoubleClick,
    handleEditingClick,
    handleTextEditConfirm,
    updateTextProperty,
    updateRectProperty,
    handleAddImage,
    refreshTransformer,
    contextMenu,
    handleObjectContextMenu,
    closeContextMenu,
    bringToFront,
    bringForward,
    sendBackward,
    sendToBack,
  } = useCanvasDrawing({
    stageRef,
    containerRef,
    transformerRef,
    sourceImg,
    currentTool,
    setCurrentTool,
    markerColor,
    markerStrokeWidth,
    rectFillEnabled,
    rectFillColor,
    rectStrokeEnabled,
    rectStrokeColor,
    textFontSize,
    textFontFamily,
    textFontStyle,
    textAlign,
    verticalAlign,
    textColor,
    textBgColor,
    onBandDelete: () => {
      // 帯テンプレートをクリア
      dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
      dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
      dispatch(floorPlanExtractionActions.setBandXOffset(0));
      dispatch(floorPlanExtractionActions.setBandYOffset(0));
      dispatch(floorPlanExtractionActions.setBandScale(1.0));
      dispatch(floorPlanExtractionActions.setBandScaleY(1.0));
    },
  });

  // テンプレート管理フック
  const {
    showNewTemplateForm,
    setShowNewTemplateForm,
    showTemplateSelectPopup,
    setShowTemplateSelectPopup,
    newTemplateName,
    setNewTemplateName,
    newTemplatePosition,
    setNewTemplatePosition,
    newTemplateFile,
    setNewTemplateFile,
    newTemplatePreview,
    setNewTemplatePreview,
    isSavingTemplate,
    editingTemplateId,
    showDeleteConfirmPopup,
    handleTemplateChange,
    handleNewTemplateFileChange,
    resetNewTemplateForm,
    handleSaveNewTemplate,
    handleRequestDeleteTemplate,
    handleConfirmDeleteTemplate,
    handleCancelDeleteTemplate,
    handleOpenEditTemplatePopup,
    handleUpdateTemplate,
    handleSelectTemplate,
    handlePositionChange,
    handleScaleChange,
  } = useBandTemplateManagement({
    bandTemplates,
    selectedBandTemplate,
  });

  // ダウンロードドロップダウン
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);

  // クレジット確認ポップアップ
  const [showCreditConfirmPopup, setShowCreditConfirmPopup] = useState(false);
  const [pendingDownloadType, setPendingDownloadType] = useState<'image' | 'pdf' | null>(null);

  // リセット確認ポップアップ
  const [showResetConfirmPopup, setShowResetConfirmPopup] = useState(false);

  // クリア確認ポップアップ
  const [showClearConfirmPopup, setShowClearConfirmPopup] = useState(false);

  // フォントサイズドロップダウン
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const fontSizeDropdownRef = useRef<HTMLDivElement>(null);

  // テキスト配置ドロップダウン
  const [showTextAlignDropdown, setShowTextAlignDropdown] = useState(false);
  const textAlignDropdownRef = useRef<HTMLDivElement>(null);

  // カラーピッカー用ref
  const textColorInputRef = useRef<HTMLInputElement>(null);
  const bgColorInputRef = useRef<HTMLInputElement>(null);
  const rectFillColorInputRef = useRef<HTMLInputElement>(null);
  const rectStrokeColorInputRef = useRef<HTMLInputElement>(null);
  const selectedRectFillColorInputRef = useRef<HTMLInputElement>(null);
  const selectedRectStrokeColorInputRef = useRef<HTMLInputElement>(null);

  // 画像追加用ref
  const addImageInputRef = useRef<HTMLInputElement>(null);

  // 帯画像URL: サーバーテンプレートまたはローカルテンプレートから取得
  const bandImageUrl = selectedBandTemplate?.image_url || localBandTemplate?.imageUrl;
  const bandImg = useLoadImage(bandImageUrl);

  // オブジェクトレンダリング用ハンドラー
  const objectHandlers: ObjectHandlers = useMemo(() => ({
    setSelectedId,
    setObjects,
    handleTextDoubleClick,
    handleRectDoubleClick,
    handleEditingClick,
    handleDragEnd,
    handleObjectContextMenu,
    editingTextId,
    editingTextValue,
    cursorVisible,
    cursorPosition,
    objects,
    loadedImages,
    refreshTransformer,
  }), [setSelectedId, setObjects, handleTextDoubleClick, handleRectDoubleClick, handleEditingClick, handleDragEnd, handleObjectContextMenu, editingTextId, editingTextValue, cursorVisible, cursorPosition, objects, loadedImages, refreshTransformer]);

  // 描画プレビュー用Props
  const drawingPreviewProps = useMemo(() => ({
    isDrawing,
    currentPoints,
    currentTool,
    markerColor,
    markerStrokeWidth,
    rectFillEnabled,
    rectFillColor,
    rectStrokeEnabled,
    rectStrokeColor,
  }), [isDrawing, currentPoints, currentTool, markerColor, markerStrokeWidth, rectFillEnabled, rectFillColor, rectStrokeEnabled, rectStrokeColor]);

  // 帯テンプレート一覧を取得
  useEffect(() => {
    dispatch(floorPlanExtractionActions.fetchBandTemplates());
  }, [dispatch]);

  // 最新の帯設定をrefに同期（ページ切り替え時に最新の値を確実に取得するため）
  useEffect(() => {
    latestBandSettingsRef.current = {
      bandTemplateId: selectedBandTemplate?.id || null,
      localBandTemplateId: localBandTemplate?.id || null,
      bandPosition: bandPosition,
      bandXOffset: bandXOffset,
      bandYOffset: bandYOffset,
      bandScale: bandScale,
      bandScaleY: bandScaleY,
    };
  }, [selectedBandTemplate, localBandTemplate, bandPosition, bandXOffset, bandYOffset, bandScale, bandScaleY]);

  // 帯テンプレートがクリアされた時に、帯画像の選択状態もクリアする
  useEffect(() => {
    if (!selectedBandTemplate && !localBandTemplate && selectedId === 'band-image') {
      setSelectedId(null);
    }
  }, [selectedBandTemplate, localBandTemplate, selectedId, setSelectedId]);

  // フォントサイズドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fontSizeDropdownRef.current && !fontSizeDropdownRef.current.contains(e.target as Node)) {
        setShowFontSizeDropdown(false);
      }
    };
    if (showFontSizeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFontSizeDropdown]);

  // テキスト配置ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (textAlignDropdownRef.current && !textAlignDropdownRef.current.contains(e.target as Node)) {
        setShowTextAlignDropdown(false);
      }
    };
    if (showTextAlignDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTextAlignDropdown]);

  // オブジェクトと帯設定の変更を履歴に保存
  useEffect(() => {
    // Undo/Redo操作中は履歴に保存しない
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    // テキスト編集中（空のテキストオブジェクトがある場合）は履歴に保存しない
    const hasEmptyTextObject = objects.some(obj => obj.type === 'text' && !obj.text);
    if (hasEmptyTextObject) {
      return;
    }

    // 現在の状態を作成
    const currentState: HistoryState = {
      objects: [...objects],
      bandTemplateId: selectedBandTemplate?.id ?? null,
      localBandTemplateId: localBandTemplate?.id ?? null,
      bandPosition: bandPosition,
      bandXOffset: bandXOffset,
      bandYOffset: bandYOffset,
      bandScale: bandScale,
      bandScaleY: bandScaleY,
    };

    // 現在の状態と最後の履歴が同じなら保存しない
    const lastHistory = history[historyIndex];
    if (JSON.stringify(currentState) === JSON.stringify(lastHistory)) {
      return;
    }

    // 新しい履歴を追加（現在位置より後の履歴は削除）
    const newHistory = [...history.slice(0, historyIndex + 1), currentState];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [objects, selectedBandTemplate, localBandTemplate, bandPosition, bandXOffset, bandYOffset, bandScale, bandScaleY]);

  // 帯のX位置を計算（中央基準 + オフセット）
  const getBandX = useCallback(() => {
    const centerX = displaySize.offsetX + (displaySize.width - displaySize.width * bandScale) / 2;
    return centerX + bandXOffset;
  }, [displaySize, bandScale, bandXOffset]);

  // 帯のY位置を計算
  const getBandY = useCallback(() => {
    if (!bandImg || !displaySize.height) return displaySize.offsetY;
    const scaledBandHeight = (bandImg.height / bandImg.width) * displaySize.width * bandScale * bandScaleY;

    if (bandPosition === 'top') {
      return displaySize.offsetY + bandYOffset;
    } else {
      return displaySize.offsetY + displaySize.height - scaledBandHeight + bandYOffset;
    }
  }, [bandImg, displaySize, bandPosition, bandYOffset, bandScale, bandScaleY]);

  // 選択中のテキストオブジェクトを取得
  const selectedTextObject = selectedId
    ? objects.find(obj => obj.id === selectedId && obj.type === 'text')
    : null;

  // 選択中の四角形オブジェクトを取得
  const selectedRectObject = selectedId
    ? objects.find(obj => obj.id === selectedId && obj.type === 'rect')
    : null;

  // 選択中のマーカー/線オブジェクトを取得
  const selectedMarkerObject = selectedId
    ? objects.find(obj => obj.id === selectedId && (obj.type === 'marker' || obj.type === 'line'))
    : null;

  // マーカーオブジェクトのプロパティ更新
  const updateMarkerProperty = (property: keyof typeof objects[0], value: any) => {
    if (!selectedMarkerObject) return;
    setObjects(prev => prev.map(obj =>
      obj.id === selectedMarkerObject.id
        ? { ...obj, [property]: value }
        : obj
    ));
  };

  // 帯のドラッグ処理
  const handleBandDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;
    // X方向のオフセットを保存
    const centerX = displaySize.offsetX + (displaySize.width - displaySize.width * bandScale) / 2;
    dispatch(floorPlanExtractionActions.setBandXOffset(node.x() - centerX));
    // Y方向のオフセットを保存
    const newY = node.y();
    const baseY = getBandY() - bandYOffset;
    dispatch(floorPlanExtractionActions.setBandYOffset(newY - baseY));
  };

  // 新しい帯をアップロード
  const handleUploadNewTemplate = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    dispatch(floorPlanExtractionActions.addBandTemplate(file));
  };

  // 全てリセット（ポップアップを表示）
  const handleReset = () => {
    setShowResetConfirmPopup(true);
  };

  // リセット確認ポップアップを閉じる
  const handleCloseResetConfirmPopup = () => {
    setShowResetConfirmPopup(false);
  };

  // リセット実行
  const handleConfirmReset = () => {
    setShowResetConfirmPopup(false);
    isUndoRedoRef.current = true; // 履歴に保存しない
    setObjects([]);
    setSelectedId(null);
    // PDFページごとのオブジェクトもリセット
    setPageObjectsMap({});
    setPageBandSettingsMap({});
    pageBandSettingsMapRef.current = {};
    // 履歴をリセット
    setHistory([initialHistoryState]);
    setHistoryIndex(0);
    dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
    dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
    dispatch(floorPlanExtractionActions.setBandPosition('bottom'));
    dispatch(floorPlanExtractionActions.setBandYOffset(0));
    dispatch(floorPlanExtractionActions.setBandScale(1.0));
    dispatch(floorPlanExtractionActions.setBandScaleY(1.0));
  };

  // クリア確認ポップアップを表示
  const handleShowClearConfirm = () => {
    setShowClearConfirmPopup(true);
  };

  // クリアを実行（確認後）
  const handleConfirmClear = () => {
    // PDF関連の状態をクリア（clearBandDataで自動的にクリアされる）
    setPageObjectsMap({});
    setPageBandSettingsMap({});
    pageBandSettingsMapRef.current = {};
    setObjects([]);
    dispatch(floorPlanExtractionActions.clearBandData());
    setShowClearConfirmPopup(false);
  };

  // クリア確認ポップアップを閉じる
  const handleCloseClearConfirm = () => {
    setShowClearConfirmPopup(false);
  };

  // 新規画像で開始（直接呼び出し用 - 内部使用）
  const handleNewImage = () => {
    // PDF関連の状態をクリア（clearBandDataで自動的にクリアされる）
    setPageObjectsMap({});
    setPageBandSettingsMap({});
    pageBandSettingsMapRef.current = {};
    setObjects([]);
    dispatch(floorPlanExtractionActions.clearBandData());
  };

  // ファイル処理のメイン関数（PDF対応）
  async function handleFile(file: File) {
    console.log('[DEBUG] handleFile called', { fileName: file.name, fileType: file.type });
    if (isPDFFile(file)) {
      // PDFの場合はすぐにモーダルを表示（ページ数チェックはモーダル内で行う）
      console.log('[DEBUG] PDF detected, opening modal');
      setPdfFile(file);
      setIsPdfModalOpen(true);
    } else {
      console.log('[DEBUG] Image file, dispatching');
      dispatch(floorPlanExtractionActions.addBandSourceImage(file));
    }
  }

  // PDFページを画像に変換して追加（単一ページ用）
  async function processPdfPage(file: File, pageNumber: number) {
    setIsProcessingPdf(true);
    try {
      const result = await convertPDFPageToImage(file, pageNumber);
      const imageFile = new File([result.blob], `${file.name.replace('.pdf', '')}_page${pageNumber}.png`, {
        type: 'image/png',
      });
      dispatch(floorPlanExtractionActions.addBandSourceImage(imageFile));
    } catch (e) {
      console.error('[DEBUG] Failed to convert PDF page:', e);
    } finally {
      setIsProcessingPdf(false);
      setIsPdfModalOpen(false);
      setPdfFile(null);
    }
  }

  // 複数ページのPDF変換処理
  async function handlePdfPagesSelect(pageNumbers: number[]) {
    if (!pdfFile) return;

    setIsProcessingPdf(true);
    setIsPdfModalOpen(false);

    try {
      const convertedPages: PdfConvertedPage[] = [];

      for (const pageNumber of pageNumbers) {
        const result = await convertPDFPageToImage(pdfFile, pageNumber);
        const imageFile = new File(
          [result.blob],
          `${pdfFile.name.replace('.pdf', '')}_page${pageNumber}.png`,
          { type: 'image/png' }
        );
        convertedPages.push({
          pageNumber,
          imageFile,
          thumbnailUrl: result.dataUrl,
        });
      }

      // 変換結果をReduxに保存
      dispatch(floorPlanExtractionActions.setPdfConvertedPages(convertedPages));
      dispatch(floorPlanExtractionActions.setCurrentPdfPageIndex(0));
      // ページごとのオブジェクトと帯設定をリセット
      setPageObjectsMap({});
      setPageBandSettingsMap({});
      pageBandSettingsMapRef.current = {};
      setObjects([]);

      // 最初のページを表示
      if (convertedPages.length > 0) {
        dispatch(floorPlanExtractionActions.addBandSourceImage(convertedPages[0].imageFile));
      }
    } catch (e) {
      console.error('[DEBUG] Failed to convert PDF pages:', e);
    } finally {
      setIsProcessingPdf(false);
      setPdfFile(null);
    }
  }

  // PDFページ切り替え
  function switchPdfPage(index: number) {
    if (index >= 0 && index < pdfConvertedPages.length && index !== currentPdfPageIndex) {
      // 現在のページのオブジェクトを保存
      setPageObjectsMap(prev => ({
        ...prev,
        [currentPdfPageIndex]: [...objects],
      }));

      // 現在のページの帯設定を保存（refから最新の値を取得）
      const currentSettings = latestBandSettingsRef.current;

      // refを直接更新（同期的）して即座に使用できるようにする
      const updatedBandSettingsMap = {
        ...pageBandSettingsMapRef.current,
        [currentPdfPageIndex]: {
          bandTemplateId: currentSettings.bandTemplateId,
          localBandTemplateId: currentSettings.localBandTemplateId,
          bandPosition: currentSettings.bandPosition,
          bandXOffset: currentSettings.bandXOffset,
          bandYOffset: currentSettings.bandYOffset,
          bandScale: currentSettings.bandScale,
          bandScaleY: currentSettings.bandScaleY,
        },
      };
      // refとstateの両方を更新
      pageBandSettingsMapRef.current = updatedBandSettingsMap;
      setPageBandSettingsMap(updatedBandSettingsMap);

      // 新しいページのオブジェクトを復元
      const newPageObjects = pageObjectsMap[index] || [];
      setObjects(newPageObjects);
      setSelectedId(null);

      // 新しいページの帯設定を復元（refから取得）
      const newBandSettings = pageBandSettingsMapRef.current[index];
      if (newBandSettings) {
        // 保存されている設定を復元
        if (newBandSettings.bandTemplateId) {
          const template = bandTemplates.find(t => t.id === newBandSettings.bandTemplateId);
          dispatch(floorPlanExtractionActions.setSelectedBandTemplate(template || null));
        } else {
          dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
        }
        dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
        dispatch(floorPlanExtractionActions.setBandPosition(newBandSettings.bandPosition));
        dispatch(floorPlanExtractionActions.setBandXOffset(newBandSettings.bandXOffset));
        dispatch(floorPlanExtractionActions.setBandYOffset(newBandSettings.bandYOffset));
        dispatch(floorPlanExtractionActions.setBandScale(newBandSettings.bandScale));
        dispatch(floorPlanExtractionActions.setBandScaleY(newBandSettings.bandScaleY));
      } else {
        // 設定がない場合はデフォルト値（帯なし）
        dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
        dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
        dispatch(floorPlanExtractionActions.setBandPosition('top'));
        dispatch(floorPlanExtractionActions.setBandXOffset(0));
        dispatch(floorPlanExtractionActions.setBandYOffset(0));
        dispatch(floorPlanExtractionActions.setBandScale(1.0));
        dispatch(floorPlanExtractionActions.setBandScaleY(1.0));
      }

      // ページを切り替え（Reduxに保存）
      dispatch(floorPlanExtractionActions.setCurrentPdfPageIndex(index));
      dispatch(floorPlanExtractionActions.addBandSourceImage(pdfConvertedPages[index].imageFile));
    }
  }

  function closePdfModal() {
    setIsPdfModalOpen(false);
    setPdfFile(null);
    clearPDFCache();
  }

  // ファイルアップロード
  const addBandSourceImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading || !event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    handleFile(file);
  };

  // ドラッグ＆ドロップ
  const handleFileDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const handleFileDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
  };

  const handleFileDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (isLoading) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFile(file);
    }
  };

  // ファイルサイズ超過ポップアップを閉じる
  const closePhotoTooBigPopup = () => {
    dispatch(floorPlanExtractionActions.setIsPhotoTooBigPopupShow(false));
  };

  // 間取り抜き出し結果を使用
  const handleUseExtractionResult = () => {
    dispatch(floorPlanExtractionActions.setBandInputSource('extraction_result'));
  };

  // 間取り抜き出し結果があるかどうか
  // 間取り抜き出し結果を帯替えで使用する機能は一旦非表示（コンフリクト回避のためコード削除せず）
  const hasExtractionResult = false; // extractionResult !== null;

  // コンテンツの実際の範囲を計算（抽出したユーティリティを使用）
  const calcContentBounds = () => getContentBounds({
    displaySize,
    bandImg,
    bandScale,
    bandScaleY,
    getBandX,
    getBandY,
  });

  // ダウンロードリクエスト（Mini/Liteプランのみクレジット確認ポップアップを表示）
  const handleRequestDownloadImage = async () => {
    setShowDownloadDropdown(false);
    if (PlanTypeExtension.isMiniOrLiteOrMiniEdit(planType)) {
      // Mini/Liteプランはクレジット消費確認を表示
      setPendingDownloadType('image');
      setShowCreditConfirmPopup(true);
    } else {
      // それ以外のプランは直接ダウンロード（履歴は記録する）
      const result = await dispatch(floorPlanExtractionActions.consumeBandReplacementCredit({
        downloadType: 'image',
        sourceFileName: bandSourceImage?.file?.name,
      }));
      // エラーが発生した場合はダウンロードを中止
      if (result.payload && !(result.payload as { success: boolean }).success) {
        return;
      }
      await executeDownloadImage();
    }
  };

  const handleRequestDownloadPDF = async () => {
    setShowDownloadDropdown(false);
    if (PlanTypeExtension.isMiniOrLiteOrMiniEdit(planType)) {
      // Mini/Liteプランはクレジット消費確認を表示
      setPendingDownloadType('pdf');
      setShowCreditConfirmPopup(true);
    } else {
      // それ以外のプランは直接ダウンロード（履歴は記録する）
      const result = await dispatch(floorPlanExtractionActions.consumeBandReplacementCredit({
        downloadType: 'pdf',
        sourceFileName: bandSourceImage?.file?.name,
      }));
      // エラーが発生した場合はダウンロードを中止
      if (result.payload && !(result.payload as { success: boolean }).success) {
        return;
      }
      await executeDownloadPDF();
    }
  };

  // クレジット確認ポップアップを閉じる
  const handleCloseCreditConfirmPopup = () => {
    setShowCreditConfirmPopup(false);
    setPendingDownloadType(null);
  };

  // 画像としてダウンロード（実際のダウンロード処理）
  const executeDownloadImage = async () => {
    if (displaySize.width > 0 && displaySize.height > 0) {
      // Transformerの選択ハンドルを一時的に非表示にする
      const prevNodes = transformerRef.current?.nodes() || [];
      transformerRef.current?.nodes([]);
      // 透かしを非表示にする
      setIsDownloading(true);
      // React再レンダリングを待つ
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const bounds = calcContentBounds();
      downloadCanvasAsImage({ stageRef, bounds });

      // Transformerの選択状態を復元
      transformerRef.current?.nodes(prevNodes);
      // 透かしを再表示
      setIsDownloading(false);
    }
  };

  // PDFとしてダウンロード（実際のダウンロード処理）
  const executeDownloadPDF = async () => {
    if (displaySize.width > 0 && displaySize.height > 0) {
      // Transformerの選択ハンドルを一時的に非表示にする
      const prevNodes = transformerRef.current?.nodes() || [];
      transformerRef.current?.nodes([]);
      // 透かしを非表示にする
      setIsDownloading(true);
      // React再レンダリングを待つ
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      try {
        // 複数ページがある場合
        if (pdfConvertedPages.length > 1) {
          // 現在のページのオブジェクトを保存
          const currentPageObjectsMap = {
            ...pageObjectsMap,
            [currentPdfPageIndex]: [...objects],
          };

          // 現在のページの帯設定を保存（refから最新の値を取得）
          const currentSettings = latestBandSettingsRef.current;
          const currentPageBandSettingsMap: Record<number, PageBandSettings> = {
            ...pageBandSettingsMapRef.current,
            [currentPdfPageIndex]: {
              bandTemplateId: currentSettings.bandTemplateId,
              localBandTemplateId: currentSettings.localBandTemplateId,
              bandPosition: currentSettings.bandPosition,
              bandXOffset: currentSettings.bandXOffset,
              bandYOffset: currentSettings.bandYOffset,
              bandScale: currentSettings.bandScale,
              bandScaleY: currentSettings.bandScaleY,
            },
          };

          const imageDataUrls: string[] = [];

          // 各ページのデータURLを取得
          for (let i = 0; i < pdfConvertedPages.length; i++) {
            if (i === currentPdfPageIndex) {
              // 現在のページは直接キャプチャ
              const bounds = calcContentBounds();
              if (stageRef.current) {
                const uri = stageRef.current.toDataURL({
                  pixelRatio: 2,
                  x: bounds.x,
                  y: bounds.y,
                  width: bounds.width,
                  height: bounds.height,
                });
                imageDataUrls.push(uri);
              }
            } else {
              // 他のページはオフスクリーンレンダリングで編集内容を含める
              const pageObjects = currentPageObjectsMap[i] || [];
              const pageBandSettings = currentPageBandSettingsMap[i];

              // ページごとの帯設定を取得
              let pageBandImageUrl: string | undefined;
              let pageBandScale = 1.0;
              let pageBandScaleY = 1.0;
              let pageBandPosition: 'top' | 'bottom' = 'top';
              let pageBandXOffset = 0;
              let pageBandYOffset = 0;

              if (pageBandSettings) {
                pageBandScale = pageBandSettings.bandScale;
                pageBandScaleY = pageBandSettings.bandScaleY;
                pageBandPosition = pageBandSettings.bandPosition;
                pageBandXOffset = pageBandSettings.bandXOffset;
                pageBandYOffset = pageBandSettings.bandYOffset;

                // 帯テンプレートのURLを取得
                if (pageBandSettings.bandTemplateId) {
                  const template = bandTemplates.find(t => t.id === pageBandSettings.bandTemplateId);
                  if (template) {
                    pageBandImageUrl = template.image_url;
                  }
                }
              }

              try {
                const dataUrl = await renderPageOffScreen({
                  sourceImageFile: pdfConvertedPages[i].imageFile,
                  displaySize,
                  objects: pageObjects,
                  bandImageUrl: pageBandImageUrl,
                  bandScale: pageBandScale,
                  bandScaleY: pageBandScaleY,
                  bandPosition: pageBandPosition,
                  bandXOffset: pageBandXOffset,
                  bandYOffset: pageBandYOffset,
                  canvasWidth: canvasSize.width,
                  canvasHeight: canvasSize.height,
                });
                imageDataUrls.push(dataUrl);
              } catch (error) {
                console.error(`Failed to render page ${i + 1}:`, error);
                // フォールバック: サムネイルを使用
                imageDataUrls.push(pdfConvertedPages[i].thumbnailUrl);
              }
            }
          }

          await downloadMultiPagePDF({ imageDataUrls, margin: 10 });
        } else {
          // 単一ページの場合は従来通り
          const bounds = calcContentBounds();
          await downloadCanvasAsPDF({ stageRef, bounds, margin: 10 });
        }
      } finally {
        // Transformerの選択状態を復元
        transformerRef.current?.nodes(prevNodes);
        // 透かしを再表示
        setIsDownloading(false);
      }
    }
  };

  // クレジット確認後のダウンロード実行
  const handleConfirmDownload = async () => {
    setShowCreditConfirmPopup(false);

    if (!pendingDownloadType) {
      return;
    }

    // クレジット消費APIを呼び出す（ダウンロードタイプを渡す）
    const result = await dispatch(floorPlanExtractionActions.consumeBandReplacementCredit({
      downloadType: pendingDownloadType,
      sourceFileName: bandSourceImage?.file?.name,
    }));

    // クレジット消費に失敗した場合はダウンロードを中止
    if (result.payload && !(result.payload as { success: boolean }).success) {
      setPendingDownloadType(null);
      return;
    }

    if (pendingDownloadType === 'image') {
      await executeDownloadImage();
    } else if (pendingDownloadType === 'pdf') {
      await executeDownloadPDF();
    }

    setPendingDownloadType(null);
  };

  // Undo機能
  const handleUndo = () => {
    if (historyIndex > 0) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const prevState = history[newIndex];
      // オブジェクトを復元
      setObjects(prevState.objects);
      setSelectedId(null);
      // 帯設定を復元
      if (prevState.bandTemplateId) {
        const template = bandTemplates.find(t => t.id === prevState.bandTemplateId);
        dispatch(floorPlanExtractionActions.setSelectedBandTemplate(template || null));
      } else {
        dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
      }
      // localBandTemplateは復元が複雑なためクリアする（既存のlocalBandTemplateがあれば保持）
      if (!prevState.localBandTemplateId) {
        dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
      }
      dispatch(floorPlanExtractionActions.setBandPosition(prevState.bandPosition));
      dispatch(floorPlanExtractionActions.setBandXOffset(prevState.bandXOffset));
      dispatch(floorPlanExtractionActions.setBandYOffset(prevState.bandYOffset));
      dispatch(floorPlanExtractionActions.setBandScale(prevState.bandScale));
      dispatch(floorPlanExtractionActions.setBandScaleY(prevState.bandScaleY));
    }
  };

  // Redo機能
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const nextState = history[newIndex];
      // オブジェクトを復元
      setObjects(nextState.objects);
      setSelectedId(null);
      // 帯設定を復元
      if (nextState.bandTemplateId) {
        const template = bandTemplates.find(t => t.id === nextState.bandTemplateId);
        dispatch(floorPlanExtractionActions.setSelectedBandTemplate(template || null));
      } else {
        dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
      }
      // localBandTemplateは復元が複雑なためクリアする
      if (!nextState.localBandTemplateId) {
        dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
      }
      dispatch(floorPlanExtractionActions.setBandPosition(nextState.bandPosition));
      dispatch(floorPlanExtractionActions.setBandXOffset(nextState.bandXOffset));
      dispatch(floorPlanExtractionActions.setBandYOffset(nextState.bandYOffset));
      dispatch(floorPlanExtractionActions.setBandScale(nextState.bandScale));
      dispatch(floorPlanExtractionActions.setBandScaleY(nextState.bandScaleY));
    }
  };

  return (
    <div className="flex flex-col h-full border border-gray-300 bg-gray-100 rounded">
      {/* PDFページ選択モーダル */}
      <PDFPageSelectModal
        isOpen={isPdfModalOpen}
        file={pdfFile}
        onSelectPages={handlePdfPagesSelect}
        onClose={closePdfModal}
        maxSelectCount={2}
      />

      {/* PDF処理中オーバーレイ */}
      {isProcessingPdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>PDFを変換中...</span>
          </div>
        </div>
      )}

      {/* ファイルサイズ超過ポップアップ */}
      {isPhotoTooBigPopupShow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">ファイルサイズエラー</h3>
            <p className="text-gray-600 mb-4">
              ファイルサイズが大きすぎます。5MB以下の画像をアップロードしてください。
            </p>
            <button
              onClick={closePhotoTooBigPopup}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* メインエリア */}
      <div className="flex-grow flex gap-2 p-2 overflow-hidden" style={{ height: 'calc(100vh - 300px)', minHeight: '300px' }}>
        {/* 左側：帯設定パネル */}
        <BandTemplatePanel
          selectedBandTemplate={selectedBandTemplate}
          localBandTemplate={localBandTemplate}
          bandPosition={bandPosition}
          bandScale={bandScale}
          onShowTemplateSelectPopup={() => setShowTemplateSelectPopup(true)}
          onPositionChange={handlePositionChange}
          onScaleChange={handleScaleChange}
          onScaleSet={(scale: number) => dispatch(floorPlanExtractionActions.setBandScale(scale))}
          onFitToWidth={() => {
            dispatch(floorPlanExtractionActions.setBandScale(1.0));
            dispatch(floorPlanExtractionActions.setBandScaleY(1.0));
            dispatch(floorPlanExtractionActions.setBandXOffset(0));
            dispatch(floorPlanExtractionActions.setBandYOffset(0));
          }}
          sourceImageUrl={sourceImageUrl}
          showDownloadDropdown={showDownloadDropdown}
          setShowDownloadDropdown={setShowDownloadDropdown}
          downloadDropdownRef={downloadDropdownRef}
          creditConsumed={creditConsumed}
          creditLimit={creditLimit}
          isLimitInfinity={isLimitInfinity}
          planType={planType}
          onCancel={handleShowClearConfirm}
          bandTemplatesCount={bandTemplatesCount}
          bandTemplatesLoading={props.bandTemplatesLoading}
          onShowNewTemplateForm={() => setShowNewTemplateForm(true)}
        />

        {/* 右側：ツールバー + キャンバスエリア */}
        <div className="flex-grow flex flex-col overflow-hidden">
          {/* ツールバー */}
          <EditorToolbar
            currentTool={currentTool}
            setCurrentTool={setCurrentTool}
            clearSelection={() => setSelectedId(null)}
            markerColor={markerColor}
            setMarkerColor={setMarkerColor}
            markerStrokeWidth={markerStrokeWidth}
            setMarkerStrokeWidth={setMarkerStrokeWidth}
            rectFillEnabled={rectFillEnabled}
            setRectFillEnabled={setRectFillEnabled}
            rectFillColor={rectFillColor}
            setRectFillColor={setRectFillColor}
            rectStrokeEnabled={rectStrokeEnabled}
            setRectStrokeEnabled={setRectStrokeEnabled}
            rectStrokeColor={rectStrokeColor}
            setRectStrokeColor={setRectStrokeColor}
            textFontFamily={textFontFamily}
            setTextFontFamily={setTextFontFamily}
            textFontSize={textFontSize}
            setTextFontSize={setTextFontSize}
            textFontStyle={textFontStyle}
            setTextFontStyle={setTextFontStyle}
            textAlign={textAlign}
            setTextAlign={setTextAlign}
            verticalAlign={verticalAlign}
            setVerticalAlign={setVerticalAlign}
            textColor={textColor}
            setTextColor={setTextColor}
            textBgColor={textBgColor}
            setTextBgColor={setTextBgColor}
            selectedTextObject={selectedTextObject}
            selectedRectObject={selectedRectObject}
            selectedMarkerObject={selectedMarkerObject || null}
            updateTextProperty={updateTextProperty}
            updateRectProperty={updateRectProperty}
            updateMarkerProperty={updateMarkerProperty}
            onReset={handleReset}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onAddImage={handleAddImage}
            canReset={historyIndex > 0}
            canUndo={historyIndex > 0}
            canRedo={historyIndex < history.length - 1}
          />

          {/* キャンバスエリア */}
          <div ref={containerRef} className="flex-grow bg-gray-300 overflow-hidden border border-gray-300 relative" onContextMenu={(e) => e.preventDefault()}>
          {!sourceImageUrl ? (
            // 画像がない場合はアップロードエリアを表示
            <FileUploadDropzone
              inputId="band-replacement-file-input"
              onFileSelect={addBandSourceImage}
              onDragOver={handleFileDragOver}
              onDragLeave={handleFileDragLeave}
              onDrop={handleFileDrop}
              hasExtractionResult={hasExtractionResult}
              onUseExtractionResult={handleUseExtractionResult}
            />
          ) : (
            <>
              {/* PDFページ切り替えUI（複数ページがある場合のみ表示） - 縦配置 - 画像読み込み中も表示 */}
              {pdfConvertedPages.length > 1 && (
                <div className="absolute top-2 right-2 z-10 bg-white rounded-lg shadow-lg p-2 flex flex-col items-center gap-2 max-h-[80%] overflow-y-auto">
                  <span className="text-sm text-gray-600">
                    {currentPdfPageIndex + 1} / {pdfConvertedPages.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => switchPdfPage(currentPdfPageIndex - 1)}
                    disabled={currentPdfPageIndex === 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="前のページ"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <div className="flex flex-col gap-1">
                    {pdfConvertedPages.map((page, index) => {
                      // 現在のページの帯設定を確認
                      const hasBandTemplate = index === currentPdfPageIndex
                        ? !!(selectedBandTemplate || localBandTemplate)
                        : !!pageBandSettingsMap[index]?.bandTemplateId;

                      return (
                        <button
                          key={page.pageNumber}
                          type="button"
                          onClick={() => switchPdfPage(index)}
                          className={`relative w-12 h-16 rounded border-2 overflow-hidden transition-all ${
                            index === currentPdfPageIndex
                              ? 'border-blue-500 ring-2 ring-blue-200'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          title={`${page.pageNumber}ページ目${hasBandTemplate ? '（帯あり）' : ''}`}
                        >
                          <img
                            src={page.thumbnailUrl}
                            alt={`ページ ${page.pageNumber}`}
                            className="w-full h-full object-cover"
                          />
                          {/* 帯設定インジケータ */}
                          {hasBandTemplate && (
                            <div className="absolute bottom-0 left-0 right-0 h-2 bg-orange-500 opacity-80" title="帯あり" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => switchPdfPage(currentPdfPageIndex + 1)}
                    disabled={currentPdfPageIndex === pdfConvertedPages.length - 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="次のページ"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}
              {sourceImg ? (
                <>
              <Stage
              ref={stageRef}
              width={canvasSize.width}
              height={canvasSize.height}
              onClick={handleStageClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <Layer>
                {/* 背景画像 */}
                <KonvaImage
                  name="background"
                  image={sourceImg}
                  x={displaySize.offsetX}
                  y={displaySize.offsetY}
                  width={displaySize.width}
                  height={displaySize.height}
                />

                {/* 帯画像 */}
                {bandImg && (selectedBandTemplate || localBandTemplate) && (
                  <KonvaImage
                    ref={bandImageRef}
                    id="band-image"
                    image={bandImg}
                    x={getBandX()}
                    y={getBandY()}
                    width={displaySize.width * bandScale}
                    height={(bandImg.height / bandImg.width) * displaySize.width * bandScale * bandScaleY}
                    draggable
                    onClick={() => setSelectedId('band-image')}
                    onTap={() => setSelectedId('band-image')}
                    onDragEnd={handleBandDragEnd}
                    onTransformEnd={(e) => {
                      const node = e.target;
                      const newScaleX = node.scaleX();
                      const newScaleYVal = node.scaleY();
                      // Transform後の実際の位置を取得
                      const finalX = node.x();
                      const finalY = node.y();
                      // スケールをリセットしてwidth/heightで管理
                      node.scaleX(1);
                      node.scaleY(1);
                      const newBandScale = Math.max(0.01, bandScale * newScaleX);
                      // 高さ = baseHeight * bandScale * bandScaleY なので、
                      // bandScaleが変わった分をbandScaleYで補正する（scaleY / scaleX）
                      const newBandScaleY = Math.max(0.01, bandScaleY * newScaleYVal / newScaleX);
                      // 新しいスケールでの基準位置を計算
                      const defaultX = displaySize.offsetX + (displaySize.width - displaySize.width * newBandScale) / 2;
                      const newScaledBandHeight = (bandImg!.height / bandImg!.width) * displaySize.width * newBandScale * newBandScaleY;
                      const defaultY = bandPosition === 'top'
                        ? displaySize.offsetY
                        : displaySize.offsetY + displaySize.height - newScaledBandHeight;
                      // オフセット = 実際の位置 - 基準位置
                      dispatch(floorPlanExtractionActions.setBandScale(newBandScale));
                      dispatch(floorPlanExtractionActions.setBandScaleY(newBandScaleY));
                      dispatch(floorPlanExtractionActions.setBandXOffset(finalX - defaultX));
                      dispatch(floorPlanExtractionActions.setBandYOffset(finalY - defaultY));
                    }}
                  />
                )}

                {/* 編集オブジェクト */}
                {objects.map(obj => renderEditorObject(obj, objectHandlers))}

                {/* 描画中のプレビュー */}
                {renderDrawingPreview(drawingPreviewProps)}

                {/* ウォーターマーク（らくデコ）- Mini/Liteプランのみ表示、ダウンロード時は非表示 */}
                {!isDownloading && PlanTypeExtension.isMiniOrLiteOrMiniEdit(planType) && <Watermark displaySize={displaySize} />}

                {/* Transformer */}
                <Transformer
                  ref={transformerRef}
                  borderEnabled={true}
                  borderStroke="#2563eb"
                  borderStrokeWidth={1}
                  keepRatio={true}
                  rotateEnabled={true}
                  rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                  rotationSnapTolerance={5}
                  boundBoxFunc={(oldBox, newBox) => {
                    if (newBox.width < 5 || newBox.height < 5) {
                      return oldBox;
                    }
                    return newBox;
                  }}
                />
              </Layer>
            </Stage>
            {/* ウォーターマーク注意書き - Mini/Liteプランのみ表示 */}
            {PlanTypeExtension.isMiniOrLiteOrMiniEdit(planType) && (
              <div className="absolute bottom-2 left-2 z-10 bg-white/80 rounded px-2 py-1">
                <p className="text-xs text-gray-500">
                  ※「らくらくデコルーム」の透かしはダウンロード時には含まれません
                </p>
              </div>
            )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-400">画像を読み込み中...</p>
                </div>
              )}
            </>
          )}

          {/* コンテキストメニュー（階層変更） */}
          {contextMenu && (
            <div
              style={{
                position: 'absolute',
                top: contextMenu.y,
                left: contextMenu.x,
                zIndex: 20,
                width: '100px',
              }}
              className="bg-white rounded-lg shadow-lg border border-gray-200 py-2"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm font-bold text-gray-800 hover:bg-gray-100"
                onClick={bringToFront}
              >
                最前面へ
              </button>
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm font-bold text-gray-800 hover:bg-gray-100"
                onClick={bringForward}
              >
                前面へ
              </button>
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-gray-100"
                onClick={sendBackward}
              >
                背面へ
              </button>
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-gray-100"
                onClick={sendToBack}
              >
                最背面へ
              </button>
            </div>
          )}

          {/* テキスト編集用テキストエリア */}
          {editingTextPosition && (
            <TextEditOverlay
              textareaRef={textareaRef}
              editingTextPosition={editingTextPosition}
              editingTextValue={editingTextValue}
              setEditingTextValue={setEditingTextValue}
              setCursorPosition={setCursorPosition}
              handleTextEditConfirm={handleTextEditConfirm}
              editingObj={objects.find(obj => obj.id === editingTextId)}
              textFontSize={textFontSize}
              textFontFamily={textFontFamily}
              textFontStyle={textFontStyle}
              textColor={textColor}
            />
          )}
        </div>
        </div>
      </div>

      {/* 新規テンプレート登録ポップアップ */}
      {showNewTemplateForm && (
        <NewTemplateForm
          newTemplateName={newTemplateName}
          setNewTemplateName={setNewTemplateName}
          newTemplatePosition={newTemplatePosition}
          setNewTemplatePosition={setNewTemplatePosition}
          newTemplatePreview={newTemplatePreview}
          onFileChange={handleNewTemplateFileChange}
          editingTemplateId={editingTemplateId}
          isSavingTemplate={isSavingTemplate}
          onCancel={resetNewTemplateForm}
          onSave={handleSaveNewTemplate}
          onUpdate={handleUpdateTemplate}
          newTemplateFile={newTemplateFile}
        />
      )}

      {/* テンプレート選択ポップアップ */}
      {showTemplateSelectPopup && (
        <BandTemplateSelectPopup
          bandTemplates={bandTemplates}
          selectedBandTemplate={selectedBandTemplate}
          localBandTemplate={localBandTemplate}
          bandTemplatesCount={bandTemplatesCount}
          bandTemplatesMaxCount={bandTemplatesMaxCount}
          onClose={() => setShowTemplateSelectPopup(false)}
          onSelectTemplate={handleSelectTemplate}
          onEditTemplate={handleOpenEditTemplatePopup}
          onDeleteTemplate={handleRequestDeleteTemplate}
          onNewTemplate={() => {
            setShowTemplateSelectPopup(false);
            setShowNewTemplateForm(true);
          }}
        />
      )}

      {/* ダウンロード形式選択ドロップダウン */}
      {showDownloadDropdown && (
        <DownloadDropdown
          anchorRef={downloadDropdownRef}
          onClose={() => setShowDownloadDropdown(false)}
          onDownloadImage={handleRequestDownloadImage}
          onDownloadPDF={handleRequestDownloadPDF}
        />
      )}

      {/* クレジット消費確認ポップアップ */}
      <Modal show={showCreditConfirmPopup} onClose={handleCloseCreditConfirmPopup} size="md">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <button type="button" onClick={handleCloseCreditConfirmPopup}
                className="absolute top-3 end-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center">
                <CloseModalIcon />
                <span className="sr-only">閉じる</span>
              </button>
              <div className="p-4 md:p-5">
                <ConfirmGenerateAfterPhotoIcon />
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  ダウンロードに1回分のクレジットを使用します。よろしいですか？<br/><br/>
                </h3>
                <div className="flex justify-between mb-1 text-gray-500">
                  <span className="text-base font-normal">実行回数</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {isLimitInfinity
                      ? `実行回数 ${creditConsumed}回/上限なし(アカウントのクレジット上限)`
                      : `残り${creditLimit - creditConsumed}回/${creditLimit}回`
                    }
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-orange-500 h-2.5 rounded-full" style={{width: isLimitInfinity ? '0%' : `${(creditConsumed / creditLimit * 100)}%`}}></div>
                </div>
                <div className="flex flex-row-reverse items-center mt-6 space-x-4">
                  <button type="button" onClick={handleConfirmDownload}
                    className="sm:ml-3 w-32 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                    ダウンロード
                  </button>
                  <button type="button" onClick={handleCloseCreditConfirmPopup}
                    className="sm:mr-3 w-28 text-gray-900 bg-white hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                    キャンセル
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* リセット確認ポップアップ */}
      <Modal show={showResetConfirmPopup} onClose={handleCloseResetConfirmPopup} size="md">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <button type="button" onClick={handleCloseResetConfirmPopup}
                className="absolute top-3 end-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center">
                <CloseModalIcon />
                <span className="sr-only">閉じる</span>
              </button>
              <div className="p-4 md:p-5">
                <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
                  追加したオブジェクトが全て削除されますがよろしいですか？
                </h3>
                <div className="flex flex-row-reverse items-center mt-6 space-x-4">
                  <button type="button" onClick={handleConfirmReset}
                    className="sm:ml-3 w-20 text-white bg-red-600 hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                    OK
                  </button>
                  <button type="button" onClick={handleCloseResetConfirmPopup}
                    className="sm:mr-3 w-28 text-gray-900 bg-white hover:bg-gray-100 border border-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                    キャンセル
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* クリア確認ポップアップ */}
      <HintPopup
        show={showClearConfirmPopup}
        title="全てのデータを削除"
        description={
          <div className="space-y-3">
            <p>アップロード済み画像と編集内容を全て削除します。</p>
            <p className="text-red-600 font-medium">
              この操作は取り消せません。
            </p>
          </div>
        }
        onClose={handleCloseClearConfirm}
        showExecuteButton={true}
        onExecute={handleConfirmClear}
        executeButtonText="削除"
      />

      {/* テンプレート削除確認ポップアップ */}
      <HintPopup
        show={showDeleteConfirmPopup}
        title="テンプレートを削除"
        description={
          <div className="space-y-3">
            <p>このテンプレートを削除しますか？</p>
            <p className="text-red-600 font-medium">
              この操作は取り消せません。
            </p>
          </div>
        }
        onClose={handleCancelDeleteTemplate}
        showExecuteButton={true}
        onExecute={handleConfirmDeleteTemplate}
        executeButtonText="削除"
      />
    </div>
  );
};

export default connect(mapStateToProps)(BandReplacementFormSome);
